/* eslint-disable no-undef */
import { feature, featureCollection, Point, geometry, Feature, FeatureCollection } from '@turf/helpers';

import {
    SET_POPUP_LOCATIONS,
    REORDER_USER_MANAGED_LOCATIONS,
    SHOW_MANAGED_LOCATIONS_POPUP,
    CLEAR_SAVED_FAVORITE_LOCATION_FOR_UNDO,
    SAVE_POPUP_LOCATION_DATA_BEFORE_DELETE,
    QUERY_LOCATION_BY_SLUG_NAME,
    GET_STATIONS_BY_LAT_LNG,
    TOGGLE_STATION_CHANGER,
    MAP_STATION_FEATURE_HOVER_ACTION,
    STATION_LIST_IS_HOVERED,
    STATION_CHANGER_LIST_ITEM_IS_HOVERED,
    MAP_ICON_HOVERED,
    RESET_STATION_CHANGER_DATA,
    IS_INITIAL_STATIONS_CHANGER_LOAD,
    IsInitialStationsChangerLoadActionType,
    ResetStationChangerDataActionType,
    OnMouseEnterOrLeaveMapIconType,
    StationChangerListActionType,
    StationChangerListItemActionType,
    LocationStationMapFeatureHoverActionType,
    UserReOrderLocationType,
    ClearSavedDataForUndoType,
    SavePopupLocationDataBeforeDeleteType,
    LocationsManagerPopupLocationsType,
    ShowManagedLocationsPopupType,
    ManageLocationsPopupUndoData,
    QueryLocationBySlugNameType,
    GetStationsByLatLngType,
    ToggleStationChangerType,
    MapFeatureActionPayload } from '../actions/manageLocationsPopupActions';

import {
    FavoriteLocation,
    Station,
    Location,
    LocationItemDetails,
    StationFeatureProperties } from '../../components/locationManagerPopup/locationManagerPopupTypes';
    
interface ManageLocationState {
    locations: FavoriteLocation[];
    showManagedLocations: boolean;
    undoData: ManageLocationsPopupUndoData;
    queryingLocationBySlugNameIsLoading: boolean;
    searchedFavoriteLocation: Location;
    searchedFavoriteLocationError: Record<string, unknown>;
    isInitialLoad: boolean;
    toggleStationChanger: {
        stationsList: Station[];
        stationsMapGeoJson: FeatureCollection;
        displayedStationsLocationData: LocationItemDetails;
        isLoadingStationList: boolean;
        lastErrorLoadingStationsList: Record<string, unknown>;
        hoveredItemData: MapFeatureActionPayload;
    };
}

const initialState: ManageLocationState = {
    locations: [],
    showManagedLocations: false,
    undoData: {
        showUndoButton: false,
        deletedFavoriteLocation: null,
        favoriteLocationsListBeforeDelete: []
    },
    queryingLocationBySlugNameIsLoading: false,
    isInitialLoad: true,
    toggleStationChanger: {
        stationsList: [],
        isLoadingStationList: false,
        lastErrorLoadingStationsList: {},
        hoveredItemData: {}
    }
};

type ManageLocationsPopupType = UserReOrderLocationType |
    LocationsManagerPopupLocationsType |
    ShowManagedLocationsPopupType |
    ClearSavedDataForUndoType |
    SavePopupLocationDataBeforeDeleteType |
    QueryLocationBySlugNameType |
    ToggleStationChangerType |
    GetStationsByLatLngType |
    LocationStationMapFeatureHoverActionType |
    StationChangerListItemActionType |
    StationChangerListActionType |
    OnMouseEnterOrLeaveMapIconType |
    ResetStationChangerDataActionType |
    IsInitialStationsChangerLoadActionType;

function generateStationGeoJsonFeatures( stationList: Station[] = [] ): FeatureCollection | null {

    const result: Feature[] = stationList.map((station, stationIndex) => {
        const { StationId, Latitude, Longitude } = station;

        const coordinates: number[] = [
            Longitude,
            Latitude
        ];

        const stationGeometry: Point = geometry('Point', coordinates);

        const properties: StationFeatureProperties  = {
            id: StationId,
            stationNumber: `${stationIndex + 1}`,
            latitude: Latitude,
            longitude: Longitude,
            defaultMapStationColor: station?.ProviderId == 3 ? '#6ec478' : '#6c6c6c'
        };

        const stationGeoJsonFeature: Feature = feature(stationGeometry, properties);

        return stationGeoJsonFeature;
    });

    if (result?.length) {
        return featureCollection(result);
    }

    return null;
}

export function manageLocationsPopup(state = initialState, action: ManageLocationsPopupType): ManageLocationState
{
    switch (action.type)
    {
        case SET_POPUP_LOCATIONS:
        case REORDER_USER_MANAGED_LOCATIONS:
        {
            return {
                ...state,
                locations: [...action.payload]
            };
        }
        case SHOW_MANAGED_LOCATIONS_POPUP:
        {
            return {
                ...state,
                showManagedLocations: action.payload
            };
        }
        case SAVE_POPUP_LOCATION_DATA_BEFORE_DELETE:
        case CLEAR_SAVED_FAVORITE_LOCATION_FOR_UNDO:
        {
            return {
                ...state,
                undoData: action.payload
            };
        }
        case QUERY_LOCATION_BY_SLUG_NAME.pending:
        {
            return {
                ...state,
                queryingLocationBySlugNameIsLoading: true
            };
        }
        case QUERY_LOCATION_BY_SLUG_NAME.fulfilled:
        {
            return {
                ...state,
                queryingLocationBySlugNameIsLoading: false,
                searchedFavoriteLocation: action.payload
            };
        }
        case QUERY_LOCATION_BY_SLUG_NAME.rejected:
        {
            return {
                ...state,
                queryingLocationBySlugNameIsLoading: false,
                searchedFavoriteLocationError: action.payload
            };
        }
        case GET_STATIONS_BY_LAT_LNG.pending:
        {
            return {
                ...state,
                toggleStationChanger: {
                    lastErrorLoadingStationsList: {},
                    stationsList: [], // defaulting to empty array when loading, we don't need old data persisting
                    isLoadingStationList: true
                }
            };
        }
        case GET_STATIONS_BY_LAT_LNG.fulfilled:
        {
            const stationsList: Station[] = action.payload;
            const transformedStationsData = generateStationGeoJsonFeatures(stationsList);

            return {
                ...state,
                toggleStationChanger: {
                    lastErrorLoadingStationsList: {},
                    stationsList: action.payload,
                    stationsMapGeoJson: transformedStationsData,
                    isLoadingStationList: false
                }
            };
        }
        case GET_STATIONS_BY_LAT_LNG.rejected:
        {
            return {
                ...state,
                toggleStationChanger: {
                    lastErrorLoadingStationsList: action.payload,
                    stationsList: [], // we don't need list to have any values when error happens, as it could be data from different a location.
                    stationsMapGeoJson: {},
                    isLoadingStationList: false
                }
            };
        }
        case TOGGLE_STATION_CHANGER:
        {
            return {
                ...state,
                toggleStationChanger: {
                    ...state.toggleStationChanger,
                    displayedStationsLocationData: action.payload
                }
            };
        }
        case MAP_STATION_FEATURE_HOVER_ACTION:
        {

            return {
                ...state,
                toggleStationChanger: {
                    ...state.toggleStationChanger,
                    hoveredItemData: {
                        ...state.toggleStationChanger.hoveredItemData,
                        ...action.payload
                    }
                }
            };
        }
        case STATION_LIST_IS_HOVERED:
        {
            return {
                ...state,
                toggleStationChanger: {
                    ...state.toggleStationChanger,
                    hoveredItemData: {
                        ...state.toggleStationChanger.hoveredItemData,
                        isStationListHovered: action.payload
                    }

                }
            };
        }
        case STATION_CHANGER_LIST_ITEM_IS_HOVERED:
        {
            return {
                ...state,
                toggleStationChanger: {
                    ...state.toggleStationChanger,
                    hoveredItemData: {
                        ...state.toggleStationChanger.hoveredItemData,
                        hoveredListItemStationId: action.payload.hoveredListItemStationId,
                        highlightHoveredListItemInMap: action.payload.isListItemHovered
                    }
                }
            };
        }
        case MAP_ICON_HOVERED:
        {
            return {
                ...state,
                toggleStationChanger: {
                    ...state.toggleStationChanger,
                    hoveredItemData: {
                        ...state.toggleStationChanger.hoveredItemData,
                        stationIdOfIconWithMouseOver: action.payload.stationId,
                        isMouseOverMapIcon: action.payload.isMouseOverMapIcon
                    }
                }
            };
        }
        case IS_INITIAL_STATIONS_CHANGER_LOAD:
        {
            return {
                ...state,
                isInitialLoad: action.payload
            };
        }
        case RESET_STATION_CHANGER_DATA:
        {
            return {
                ...state,
                toggleStationChanger: {} // empty this so we no longer have maps nor arrow pointing up or any mouse events that had happened on mouse move/click, just nothing
            };
        }

    }

    return state;
}